import React from "react";
import { UseFormReturn } from "react-hook-form";

import {
  Button,
  Card,
  Checkbox,
  Combobox,
  FloatingLabelInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components";
import { useI18n } from "@/context/i18n";

import { TCampaignsFormData } from "../../campaigns.schema";
import {
  TCampaignPlanOption,
  TProjectOption,
} from "../../campaigns.types";

export interface ICampaignFormProps {
  open: boolean;
  form: UseFormReturn<TCampaignsFormData>;
  onSubmit: (values: TCampaignsFormData) => void;
  onCancel: () => void;
  isEditing: boolean;
  projects: TProjectOption[];
  plans: TCampaignPlanOption[];
  isProjectsLoading: boolean;
  isPlansLoading: boolean;
}

export const CampaignForm: React.FC<ICampaignFormProps> = ({
  open,
  form,
  onSubmit,
  onCancel,
  isEditing,
  projects,
  plans,
  isProjectsLoading,
  isPlansLoading,
}) => {
  const { t } = useI18n();
  const selectedProjectId = form.watch("project_id");

  if (!open) {
    return null;
  }

  const statusOptions = [
    {
      value: "draft",
      label: t("campaigns.form.fields.status.options.draft"),
    },
    {
      value: "active",
      label: t("campaigns.form.fields.status.options.active"),
    },
    {
      value: "paused",
      label: t("campaigns.form.fields.status.options.paused"),
    },
  ];

  const planOptions = plans.map((plan) => ({
    value: plan.id,
    label: plan.name,
  }));

  const plansDisabled = !selectedProjectId || isPlansLoading;

  return (
    <Card className="p-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FloatingLabelInput
                      label={t("campaigns.form.fields.name")}
                      placeholder=" "
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="project_id"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Combobox
                      label={t("campaigns.form.fields.project")}
                      placeholder={
                        isProjectsLoading
                          ? t("generic.loading")
                          : t("campaigns.form.fields.projectPlaceholder")
                      }
                      options={projects}
                      value={field.value || undefined}
                      onChange={(value) => {
                        field.onChange(value ?? "");
                        form.setValue("plan_ids", []);
                      }}
                      disabled={isProjectsLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="plan_ids"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>{t("campaigns.form.fields.plan")}</FormLabel>
                  <div className="space-y-2 rounded-md border p-3 max-h-40 overflow-auto">
                    {!selectedProjectId && (
                      <p className="text-sm text-muted-foreground">
                        {t("campaigns.form.noProjectSelected")}
                      </p>
                    )}
                    {selectedProjectId && isPlansLoading && (
                      <p className="text-sm text-muted-foreground">
                        {t("generic.loading")}
                      </p>
                    )}
                    {selectedProjectId &&
                      !isPlansLoading &&
                      planOptions.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                          {t("campaigns.form.noPlansForProject")}
                        </p>
                    )}
                    {selectedProjectId &&
                      !isPlansLoading &&
                      planOptions.map((planOption) => (
                        <div
                          key={planOption.value}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            checked={field.value?.includes(planOption.value)}
                            onCheckedChange={(checked) => {
                              const currentValues = field.value || [];
                              if (checked === true) {
                                field.onChange([
                                  ...currentValues,
                                  planOption.value,
                                ]);
                                return;
                              }
                              field.onChange(
                                currentValues.filter(
                                  (value) => value !== planOption.value
                                )
                              );
                            }}
                            disabled={plansDisabled}
                          />
                          <label className="text-sm text-foreground">
                            {planOption.label}
                          </label>
                        </div>
                      ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="channel"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FloatingLabelInput
                      label={t("campaigns.form.fields.channel")}
                      placeholder=" "
                      value={field.value}
                      disabled
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Combobox
                      label={t("campaigns.form.fields.status.label")}
                      placeholder={t("campaigns.form.fields.status.label")}
                      options={statusOptions}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value || "draft");
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              {t("generic.cancel")}
            </Button>
            <Button
              type="submit"
              className="w-full md:w-auto"
              disabled={
                isProjectsLoading ||
                plansDisabled ||
                (Boolean(selectedProjectId) && planOptions.length === 0)
              }
            >
              {isEditing ? t("generic.edit") : t("generic.register")}
            </Button>
          </div>
        </form>
      </Form>
    </Card>
  );
};
